import { BaseOptions } from '../core/component';
import { Utils, controllerFor, restoreAttribute } from '../core/utils';

export interface SnackbarOptions extends BaseOptions {
  /**
   * The content of the Snackbar.
   * @default ""
   */
  text: string;
  /**
   * Optional action label. Rendered as a trailing text button.
   * @default ""
   */
  action: string;
  /**
   * Called when the action button is pressed. The snackbar still dismisses.
   * @default null
   */
  onAction: () => void;
  /**
   * Show a trailing close icon button.
   * Defaults to true when action is set, otherwise false.
   */
  dismissible: boolean;
  /**
   * Element id of a `<template>` (or another element) used as the snackbar body.
   * @default ""
   */
  snackbarId?: string;
  /**
   * Length in ms the Snackbar stays before dismissal.
   * `Infinity` keeps it on screen until the user acts.
   * When `action` is set and this is omitted, the default is Infinity.
   * @default 4000
   */
  displayLength: number;
  /**
   * Transition in duration in milliseconds.
   * @default 300
   */
  inDuration: number;
  /**
   * Transition out duration in milliseconds.
   * @default 375
   */
  outDuration: number;
  /**
   * Classes to be added to the snackbar element.
   * @default ""
   */
  classes: string;
  /**
   * Callback function called when the snackbar is dismissed.
   * @default null
   */
  completeCallback: () => void;
  /**
   * The percentage of the snackbar's width it takes for a drag
   * to dismiss a Snackbar.
   * @default 0.8
   */
  activationPercent: number;
  /**
   * Any element in the tree the snackbar should render into. A Snackbar has no
   * originating element to infer this from, so pass one when the page lives in
   * a shadow root — the container is appended to that root instead of to
   * `document.body`, where a root-scoped stylesheet could not reach it.
   * @default null
   */
  root: HTMLElement;
  /**
   * Show at once, dismissing the snackbar on screen without its exit
   * animation, instead of waiting in the queue behind it.
   * @default false
   */
  replace: boolean;
}

const _defaults: SnackbarOptions = {
  text: '',
  action: '',
  onAction: null,
  dismissible: false,
  displayLength: 4000,
  inDuration: 300,
  outDuration: 375,
  classes: '',
  completeCallback: null,
  activationPercent: 0.8,
  root: null,
  replace: false
};

export class Snackbar {
  /** The snackbar element. */
  el: HTMLElement;
  /**
   * The remaining amount of time in ms that the snackbar
   * will stay before dismissal.
   */
  timeRemaining: number;
  /**
   * Describes the current pan state of the Snackbar.
   */
  panning: boolean;
  options: SnackbarOptions;
  message: string;
  counterTimeout: ReturnType<typeof setTimeout>;
  /** Wall-clock time the snackbar is due to dismiss, for pause/resume. */
  private _deadline: number;
  wasSwiped: boolean;
  startingXPos: number;
  xPos: number;
  time: number;
  deltaX: number;
  velocityX: number;
  /** Swipe distance that dismisses, measured once when the drag starts. */
  private _activationDistance: number;
  private _dismissed = false;
  private _returnFocus: HTMLElement | null = null;
  /** The document body or shadow root the snackbar renders into. */
  private _portal: HTMLElement | ShadowRoot;
  private _events: AbortController;
  private _restoreElement: (() => void) | null = null;
  private _transitionTimer: ReturnType<typeof setTimeout>;
  private _dismissTimer: ReturnType<typeof setTimeout>;
  private _focusTimer: ReturnType<typeof setTimeout>;

  /** The snackbar on screen, kept until its exit animation finishes. */
  static _snackbars: Snackbar[];
  /** Snackbars waiting for the one on screen to leave, oldest first. */
  static _queue: Snackbar[];
  /** Set while a replacement clears the screen; the queue waits for it. */
  private static _replacing = false;
  static _container: HTMLElement;
  static _draggedSnackbar: Snackbar;
  /** Which pointer owns the drag in flight; a second finger is ignored. */
  static _dragPointerId: number | null = null;

  /**
   * Whether a bar gets its close button when the page does not say. One with
   * an action waits for it, and one that never times out waits for the
   * reader; either way, swiping must not be the only way to send it off.
   */
  private static _needsClose(options: SnackbarOptions): boolean {
    return !!options.action || options.displayLength === Infinity;
  }

  constructor(options: Partial<SnackbarOptions> = {}) {
    this.options = {
      ...Snackbar.defaults,
      ...options
    };
    if (options.displayLength === undefined && this.options.action) {
      this.options.displayLength = Infinity;
    }
    if (options.dismissible === undefined) this.options.dismissible = Snackbar._needsClose(this.options);
    this.message = this.options.text;
    this.panning = false;
    this.timeRemaining = this.options.displayLength;
    // Worked out now: `root` may be inside the snackbar this one replaces,
    // and gone by the time it shows.
    this._portal = Utils.portalRoot(this.options.root ?? document.body);
    // First, so an authored body a re-render orphaned is back in its place.
    Snackbar._dropDetached();
    // An authored body is taken when the snackbar shows: one waiting in the
    // queue must not claim the element the snackbar on screen is using.
    if (this.options.snackbarId) this._requireBody();
    else this._build();
    // One snackbar at a time, as Material asks. Everything goes through the
    // queue, so arrivals show in order; a replacement goes to its front once
    // the screen is cleared.
    if (this.options.replace) Snackbar._replaceShown(this);
    else Snackbar._queue.push(this);
    Snackbar._advance();
  }

  /**
   * Lets go of snackbars a re-render took out of the page. A persistent one
   * would otherwise hold the queue for good.
   */
  private static _dropDetached() {
    for (const snackbar of Snackbar._snackbars.filter((shown) => !shown.el.isConnected)) {
      snackbar._dismissed = true;
      snackbar._remove(false);
    }
  }

  /** Throws now, rather than when the snackbar shows, if `snackbarId` names nothing. */
  private _requireBody() {
    if (!this._hasBody()) throw new Error('Snackbar body not found');
  }

  /** Whether the body a snackbar shows is still there: its own, or `snackbarId`'s. */
  private _hasBody(): boolean {
    return !this.options.snackbarId || !!Utils.getElementById(this._portal, this.options.snackbarId);
  }

  private _build() {
    this.el = this._createSnackbar();
    this.el['Expressive_Snackbar'] = this;
  }

  /**
   * Takes the snackbar on screen down at once, without starting the queue.
   * Restoring focus can open another, which goes too.
   */
  private static _replaceShown(replacement: Snackbar) {
    // A snackbar opened by the focus restore below must not start the queue,
    // or the next waiting one would show only to be cut short here.
    Snackbar._replacing = true;
    try {
      while (Snackbar._snackbars.length) {
        for (const snackbar of Snackbar._snackbars.splice(0)) {
          snackbar._dismissed = true;
          snackbar._restoreFocus(true);
        }
      }
    } finally {
      Snackbar._replacing = false;
    }
    Snackbar._queue.unshift(replacement);
  }

  /** Puts the snackbar on screen and starts its countdown. */
  private _show() {
    if (!this.el) this._build();
    this._returnFocus = Snackbar._focused();
    Snackbar._containerIn(this._portal).appendChild(this.el);
    Snackbar._snackbars.push(this);
    this._animateIn();
    this._setTimer();
    Utils.emit(this.el, 'open');
  }

  /** The focused element, looking inside open shadow roots. */
  private static _focused(): HTMLElement | null {
    let focused = document.activeElement;
    while (focused?.shadowRoot?.activeElement) focused = focused.shadowRoot.activeElement;
    return focused instanceof HTMLElement ? focused : null;
  }

  /**
   * One container serves every snackbar, and only one snackbar shows at a
   * time — so it is moved rather than duplicated when the root changes.
   */
  private static _containerIn(root: HTMLElement | ShadowRoot): HTMLElement {
    const container = Snackbar._container;
    if (container?.isConnected && container.parentNode === root) return container;
    Snackbar._removeContainer();
    Snackbar._createContainer(root);
    return Snackbar._container;
  }

  static get defaults(): SnackbarOptions {
    return _defaults;
  }

  static getInstance(el: HTMLElement): Snackbar {
    return el['Expressive_Snackbar'];
  }

  static _createContainer(root: HTMLElement | ShadowRoot = document.body) {
    const container = document.createElement('div');
    container.setAttribute('id', 'snackbar-container');
    // One listener per stage. These used to be a touch trio on the container
    // plus a mouse trio split across the container and the document, with a
    // helper picking `targetTouches[0].clientX` or `clientX` apart afterwards.
    // Move and up stay on the document so a drag that leaves the container
    // still completes.
    container.addEventListener('pointerdown', Snackbar._onDragStart);
    document.addEventListener('pointermove', Snackbar._onDragMove);
    document.addEventListener('pointerup', Snackbar._onDragEnd);
    document.addEventListener('pointercancel', Snackbar._onDragCancel);
    root.appendChild(container);
    Snackbar._container = container;
  }

  static _removeContainer() {
    document.removeEventListener('pointermove', Snackbar._onDragMove);
    document.removeEventListener('pointerup', Snackbar._onDragEnd);
    document.removeEventListener('pointercancel', Snackbar._onDragCancel);
    Snackbar._container?.remove();
    Snackbar._container = null;
  }

  static _onDragStart(e: PointerEvent) {
    if (!e.isPrimary) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const target = e.target as HTMLElement | null;
    // Don't start a swipe from the action or close — those are buttons.
    if (target && !target.closest('button, a') && target.closest('.snackbar')) {
      const snackbarElem = target.closest('.snackbar') as HTMLElement;
      const snackbar: Snackbar = snackbarElem['Expressive_Snackbar'];
      if (!snackbar) return;
      snackbar.panning = true;
      snackbar._pauseTimer();
      Snackbar._draggedSnackbar = snackbar;
      snackbar.el.classList.add('panning');
      snackbar.el.style.transition = '';
      Snackbar._dragPointerId = e.pointerId;
      snackbar.startingXPos = e.clientX;
      snackbar.velocityX = 0;
      snackbar.time = Date.now();
      snackbar.xPos = e.clientX;
      // A translateX does not change the width, so one read covers the drag.
      snackbar._activationDistance = snackbar.el.offsetWidth * snackbar.options.activationPercent;
    }
  }

  static _onDragMove(e: PointerEvent) {
    if (Snackbar._dragPointerId !== e.pointerId) return;
    if (!!Snackbar._draggedSnackbar) {
      e.preventDefault();
      const snackbar = Snackbar._draggedSnackbar;
      snackbar.deltaX = Math.abs(snackbar.xPos - e.clientX);
      snackbar.xPos = e.clientX;
      // Two moves in the same millisecond would divide by zero, and an
      // infinite velocity dismisses on release.
      snackbar.velocityX = snackbar.deltaX / Math.max(1, Date.now() - snackbar.time);
      snackbar.time = Date.now();

      const totalDeltaX = snackbar.xPos - snackbar.startingXPos;
      const activationDistance = snackbar._activationDistance;
      snackbar.el.style.transform = `translateX(${totalDeltaX}px)`;
      snackbar.el.style.opacity = (1 - Math.abs(totalDeltaX / activationDistance)).toString();
    }
  }

  static _onDragEnd(e: PointerEvent) {
    if (Snackbar._dragPointerId !== e.pointerId) return;
    Snackbar._dragPointerId = null;
    if (!!Snackbar._draggedSnackbar) {
      const snackbar = Snackbar._draggedSnackbar;
      snackbar.panning = false;
      snackbar.el.classList.remove('panning');

      const totalDeltaX = snackbar.xPos - snackbar.startingXPos;
      const activationDistance = snackbar._activationDistance;
      const velocity = Date.now() - snackbar.time < 100 ? snackbar.velocityX : 0;
      const shouldBeDismissed = Math.abs(totalDeltaX) > activationDistance || velocity > 1;

      // Remove snackbar
      if (shouldBeDismissed) {
        snackbar.wasSwiped = true;
        snackbar.dismiss();
        // Animate snackbar back to original position
      } else {
        snackbar._snapBack();
      }
      Snackbar._draggedSnackbar = null;
    }
  }

  /**
   * A cancelled pointer is not a short swipe. Snap back and resume the timer
   * without consulting the distance travelled - the browser took the gesture
   * over, so the movement so far says nothing about what the user wanted.
   */
  static _onDragCancel(e: PointerEvent) {
    if (Snackbar._dragPointerId !== e.pointerId) return;
    Snackbar._dragPointerId = null;
    const snackbar = Snackbar._draggedSnackbar;
    if (!snackbar) return;
    snackbar.panning = false;
    snackbar.el.classList.remove('panning');
    snackbar._snapBack();
    Snackbar._draggedSnackbar = null;
  }

  private _snapBack() {
    this.el.style.transition = 'transform .2s, opacity .2s';
    this.el.style.transform = '';
    this.el.style.opacity = '';
    this._resumeTimer();
  }

  /**
   * dismiss all snackbars.
   */
  static dismissAll() {
    // The queue first, so the one on screen does not hand over to it. Copy
    // first: dismiss() splices the lists it is iterating.
    for (const snackbar of [...Snackbar._queue, ...Snackbar._snackbars]) {
      snackbar.dismiss();
    }
  }

  /**
   * Whether dismiss() is done already: the snackbar was dismissed before, or
   * never showed and is dropped from the queue. A dropped one reports through
   * completeCallback but fires no events, never having been in the page.
   */
  private _leaveQueue(): boolean {
    if (this._dismissed) return true;
    const index = Snackbar._queue.indexOf(this);
    if (index < 0) return false;
    Snackbar._queue.splice(index, 1);
    this._drop();
    return true;
  }

  private _drop() {
    this._dismissed = true;
    // An authored body was never taken, so there is nothing to give back.
    if (this.el) this._remove(false);
    this.options.completeCallback?.();
  }

  _createSnackbar() {
    let snackbar: HTMLElement = this.options.snackbarId
      ? Utils.getElementById(this._portal, this.options.snackbarId)
      : document.createElement('div');
    if (snackbar instanceof HTMLTemplateElement) {
      const node = snackbar.content.cloneNode(true);
      snackbar = (node as HTMLElement).firstElementChild as HTMLElement;
    } else if (snackbar && this.options.snackbarId) {
      const placeholder = document.createComment('');
      snackbar.before(placeholder);
      const children = [...snackbar.childNodes];
      const attributes = ['class', 'style', 'role', 'aria-live', 'aria-atomic', 'inert']
        .map(name => [name, snackbar.getAttribute(name)]);
      this._restoreElement = () => {
        snackbar.replaceChildren(...children);
        for (const [name, value] of attributes) restoreAttribute(snackbar, name, value);
        placeholder.replaceWith(snackbar);
      };
    }
    if (!snackbar) throw new Error('Snackbar body not found');
    this._events = controllerFor(snackbar);
    const listenerOptions = { signal: this._events.signal };
    snackbar.classList.add('snackbar');
    snackbar.setAttribute('role', 'status');
    snackbar.setAttribute('aria-live', 'polite');
    snackbar.setAttribute('aria-atomic', 'true');
    snackbar.inert = false;
    snackbar.addEventListener('focusin', (e) => {
      if (e.relatedTarget instanceof HTMLElement && !snackbar.contains(e.relatedTarget)) {
        this._returnFocus = e.relatedTarget;
      }
      this._pauseTimer();
    }, listenerOptions);
    snackbar.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || this._dismissed) return;
      e.preventDefault();
      e.stopPropagation();
      this.dismiss();
    }, listenerOptions);
    snackbar.addEventListener('focusout', () => {
      clearTimeout(this._focusTimer);
      this._focusTimer = setTimeout(() => this._resumeTimer(), 0);
    }, listenerOptions);
    snackbar.addEventListener('pointerenter', () => this._pauseTimer(), listenerOptions);
    snackbar.addEventListener('pointerleave', () => this._resumeTimer(), listenerOptions);
    // Add custom classes onto snackbar
    if (this.options.classes.length > 0) {
      snackbar.classList.add(...this.options.classes.split(' ').filter(Boolean));
    }
    if (this.message) {
      const text = document.createElement('p');
      text.textContent = this.message;
      snackbar.replaceChildren(text);
    }
    if (this.options.action) {
      const action = document.createElement('button');
      action.type = 'button';
      action.textContent = this.options.action;
      action.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this._dismissed) return;
        try {
          if (typeof this.options.onAction === 'function') this.options.onAction();
          Utils.emit(this.el, 'action');
        } finally {
          this.dismiss();
        }
      }, listenerOptions);
      snackbar.appendChild(action);
    }
    if (this.options.dismissible) {
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'circle';
      close.setAttribute('aria-label', 'Dismiss');
      const icon = document.createElement('span');
      icon.className = 'material-symbols';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = 'close';
      close.appendChild(icon);
      close.addEventListener('click', (e) => {
        e.stopPropagation();
        this.dismiss();
      }, listenerOptions);
      snackbar.appendChild(close);
    }
    return snackbar;
  }

  private _remove(advance = true) {
    if (this._events.signal.aborted) return;
    this._events.abort();
    clearTimeout(this.counterTimeout);
    clearTimeout(this._transitionTimer);
    clearTimeout(this._dismissTimer);
    clearTimeout(this._focusTimer);
    this.counterTimeout = null;
    if (Snackbar._draggedSnackbar === this) {
      Snackbar._draggedSnackbar = null;
      Snackbar._dragPointerId = null;
    }
    this.el['Expressive_Snackbar'] = undefined;
    if (this._restoreElement) this._restoreElement();
    else this.el.remove();
    const index = Snackbar._snackbars.indexOf(this);
    if (index >= 0) Snackbar._snackbars.splice(index, 1);
    if (advance) Snackbar._advance();
  }

  /** Shows the next queued snackbar once the screen is clear, or tidies up. */
  private static _advance() {
    while (!Snackbar._replacing && !Snackbar._snackbars.length) {
      const next = Snackbar._queue.shift();
      if (!next) return Snackbar._tidy();
      Snackbar._showOrDrop(next);
    }
  }

  /**
   * A queued snackbar completes without showing when the page it was for has
   * gone: its `root`, such as a removed shadow host, or its authored body.
   * Shown there, nothing could reach it to dismiss it, and the queue would stall.
   */
  private static _showOrDrop(next: Snackbar) {
    if (Snackbar._canShow(next)) next._show();
    else next._drop();
  }

  private static _canShow(next: Snackbar): boolean {
    if (!next._portal.isConnected) return false;
    return !!next.el || next._hasBody();
  }

  /** Removes the container once nothing is left in it. */
  private static _tidy() {
    if (Snackbar._container?.childElementCount === 0) Snackbar._removeContainer();
  }

  _animateIn() {
    this.el.style.display = '';
    this.el.style.opacity = '0';
    this.el.style.transform = 'translateY(24px)';
    this.el.style.transition = `
      transform ${this.options.inDuration}ms ease,
      opacity ${this.options.inDuration}ms ease
    `;
    this._transitionTimer = setTimeout(() => {
      this.el.style.transform = '';
      this.el.style.opacity = '1';
    }, 1);
  }

  /**
   * Arm the dismissal timer.
   *
   * One timeout, not a 20ms interval ticking a counter down: the old form woke
   * the page up 50 times a second per snackbar purely to subtract 20 from a
   * number. Dragging pauses the countdown via _pauseTimer/_resumeTimer instead.
   */
  _setTimer() {
    if (this.timeRemaining === Infinity) return;
    this._deadline = Date.now() + this.timeRemaining;
    this.counterTimeout = setTimeout(() => this.dismiss(), this.timeRemaining);
  }

  /** Stop the countdown, banking the time that was left on it. */
  _pauseTimer() {
    if (this.counterTimeout == null) return;
    clearTimeout(this.counterTimeout);
    this.counterTimeout = null;
    this.timeRemaining = Math.max(0, this._deadline - Date.now());
  }

  /** Restart a countdown stopped by {@link _pauseTimer}. */
  _resumeTimer() {
    if (this._dismissed || this.panning || this.el.matches(':hover, :focus-within') || this.counterTimeout != null) return;
    this._setTimer();
  }

  private _restoreFocus(remove = false) {
    const focused = (this.el.getRootNode() as Document | ShadowRoot).activeElement;
    const returnFocus = focused && this.el.contains(focused) ? this._returnFocus : null;
    this.el.inert = true;
    if (remove) this._remove(false);
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  }

  /**
   * Dismiss snackbar with animation.
   */
  dismiss() {
    if (this._leaveQueue()) return;
    this._dismissed = true;
    clearTimeout(this.counterTimeout);
    this.counterTimeout = null;
    this._restoreFocus();
    if (this._events.signal.aborted) return;
    Utils.emit(this.el, 'close');
    const duration = this.options.outDuration;
    const transitions = [`margin ${duration}ms ease`, `opacity ${duration}ms ease`];

    if (this.wasSwiped) {
      // Carry on off the side the user swiped toward, from where they let go.
      const direction = this.xPos < this.startingXPos ? -1 : 1;
      this.el.style.transform = `translateX(${direction * this.el.offsetWidth}px)`;
      this.el.style.opacity = '0';
      transitions.push(`transform ${duration}ms ease`);
    }

    this.el.style.transition = transitions.join(', ');

    clearTimeout(this._transitionTimer);
    this._transitionTimer = setTimeout(() => {
      this.el.style.opacity = '0';
      this.el.style.marginTop = '-40px';
    }, 1);

    this._dismissTimer = setTimeout(() => {
      // While the snackbar is still in the page, so the event reaches the document.
      Utils.emit(this.el, 'closed');
      this._remove();
      // Call the optional callback
      if (typeof this.options.completeCallback === 'function') {
        this.options.completeCallback();
      }
    }, this.options.outDuration);
  }

  static {
    Snackbar._snackbars = [];
    Snackbar._queue = [];
    Snackbar._container = null;
    Snackbar._draggedSnackbar = null;
  }
}
